/**
 * survey-api answers every error as RFC 9457 ProblemDetails carrying a stable
 * `type` slug. The contract is explicit that clients switch on that slug and
 * never on `title` text, so the slugs live here as constants and nowhere else.
 *
 * Source of truth: `src/Veritant.Surveys.Api/Endpoints/ProblemTypes.cs`.
 */
export const ProblemType = {
  revisionConflict: 'urn:veritant-surveys:problem:revision-conflict',
  responseNotWritable: 'urn:veritant-surveys:problem:response-not-writable',
  responseExists: 'urn:veritant-surveys:problem:response-exists',
  /** The cycle is not open, so nothing may be started in it or written to it (SR-213). A
   *  409 rather than `response-not-writable`: that one carries the *response's* status, and
   *  a closed cycle is not one. The two are different screens — "your answers are locked"
   *  and "this cycle is over" — which is why survey-api refused to collapse them. */
  cycleClosed: 'urn:veritant-surveys:problem:cycle-closed',
  writesRejected: 'urn:veritant-surveys:problem:writes-rejected',
  submitBlocked: 'urn:veritant-surveys:problem:submit-blocked',
  invalidAction: 'urn:veritant-surveys:problem:invalid-action',
  /** The admin surface: a malformed query string — bad page, unknown code, unknown status. */
  invalidQuery: 'urn:veritant-surveys:problem:invalid-query',
  /** The admin surface: a body that fails its shape check before any action is attempted. */
  invalidBody: 'urn:veritant-surveys:problem:invalid-body',
  /** A review flag already resolved or dismissed — a repeated transition is a conflict. */
  flagNotOpen: 'urn:veritant-surveys:problem:flag-not-open',
  /** An edit or a delete of a staff note by anybody but its author, an admin included, 403
   *  (SR-222). Unlike every other refusal on the surface it has a body. */
  notNoteAuthor: 'urn:veritant-surveys:problem:not-note-author',
  /** A staff link offered to a participant some account already acts for, 409 (survey-api's
   *  self-service plan, R4). The link gives a participant with no way in its first one; a
   *  second account for one participant would be delegation, which nothing has ruled on. */
  participantHasAccount: 'urn:veritant-surveys:problem:participant-has-account',

  // The authoring surface (SR-123). Four refusals a definition editor has to tell apart,
  // because each one asks the author for something different.

  /** A publish the validator stopped, 422. Carries the same `findings[]` the dry run
   *  returns — so one renderer draws both, and a failed publish reads as a dry run that
   *  happened to be attempted. Nothing was written. */
  publishFailed: 'urn:veritant-surveys:problem:publish-failed',
  /** A write reached a **published or retired** definition, 409. Not a concurrency problem:
   *  re-reading and retrying cannot help, because the freeze is permanent. */
  definitionFrozen: 'urn:veritant-surveys:problem:definition-frozen',
  /** A code collides inside its uniqueness scope, or a delete is refused because something
   *  still references the row, 409. Reachable outside the authoring surface too: two starts
   *  of the same survey racing each other leave the loser here — with `target: "code"` and
   *  no `responseId`, since the unique index has no id to hand back — rather than on
   *  `response-exists`. */
  codeInUse: 'urn:veritant-surveys:problem:code-in-use',
  /** A mutation under `/versions/{id}` arrived with no `If-Match`, 428. A bug in this app
   *  rather than something an author did — every such call goes through `definitions-api.ts`,
   *  which cannot omit it. */
  preconditionRequired: 'urn:veritant-surveys:problem:precondition-required',
  /** A lifecycle move the row's state does not offer, 409 — retiring a draft, and a work item's
   *  decision, follow-up or reopening (SR-222). Not `definition-frozen`: nothing is frozen, the
   *  move is simply not one this state takes. A work item's carries `reason` and `itemIds` —
   *  `transitionRefusal` in `domains/admin/work-items.ts` reads them; a refresh run's
   *  cancellation carries `reason`, the state it is in (`refreshRefusal`). */
  invalidTransition: 'urn:veritant-surveys:problem:invalid-transition',

  // The media library (WEB-09). Three refusals of a file rather than of a body, each judged
  // before the library is even looked at, and each asking the editor for something different.

  /** An upload with no bytes, 400. */
  imageEmpty: 'urn:veritant-surveys:problem:image-empty',
  /** An upload over 5 MB, 413. Refused on the declared length, so the file is never read. */
  imageTooLarge: 'urn:veritant-surveys:problem:image-too-large',
  /** Not a JPEG, PNG, GIF or WebP, declared as another of them, or a header survey-api cannot
   *  read a size out of, 415. Its own slug rather than the participant photo's, so a client
   *  need not know which route it called. */
  imageUnsupportedType: 'urn:veritant-surveys:problem:image-unsupported-type',

  // The registration surface (SR-213). All four are 409. The first two are about the run
  // rather than the caller, which is the only thing an anonymous surface can talk about; the
  // last two answer a signed-in caller, about a number that is their own.

  /** A step arrived that the run is not on — a retried submit, or a tab left open while the
   *  run moved on. Carries the `step` it is actually on and its run `status`, so a client
   *  re-renders rather than starting over. */
  registrationOutOfStep: 'urn:veritant-surveys:problem:registration-out-of-step',
  /** The run cannot continue with the value it was given. `reason` is about the value:
   *  `identifier_unavailable` (the run can go no further — the anchor abandons it, and the
   *  completion's lost race leaves it stuck) or `address_unavailable` (it stays open for another
   *  address). A taken number names its holder only by `heldBy`, the holder's own addresses
   *  masked (SR-487, PFR §2.4), to which the refusal has queued an invitation to sign in. */
  registrationRefused: 'urn:veritant-surveys:problem:registration-refused',
  /** A signed-in registration offered a number the caller's own account already holds, 409
   *  (survey-api's teams plan R19). Unlike `registration-refused` it names the participant —
   *  `participantId` — since the holder is the caller. The run stays at the anchor. */
  identifierAlreadyYours: 'urn:veritant-surveys:problem:identifier-already-yours',
  /** A signed-in numbers run of an account that is several people (SR-487, PFR D16): which of
   *  them the number is cannot be told, and registration never makes another person, so staff
   *  add it by hand. */
  severalPersons: 'urn:veritant-surveys:problem:several-persons',

  // The teams block (SR-213, roadmap block 7).

  /** A team's routing was decided by Veritant (`source: 'staff'`) or by its firm (`'firm'`), so the
   *  team's own answers are no longer asked for, 409. */
  teamRoutingClosed: 'urn:veritant-surveys:problem:team-routing-closed',
  /** A start for a team whose routing sends it to the RIA firm survey, 409. */
  teamRoutedToRia: 'urn:veritant-surveys:problem:team-routed-to-ria',
  /** A start in a `listed` cycle whose access list does not admit the participant, which holds
   *  no response in it, 409 (SR-487, PFR §6). Its own slug beside `cycle-closed`: the cycle is
   *  open, and staff can admit the participant. */
  cycleNotOpen: 'urn:veritant-surveys:problem:cycle-not-open',
  /** A start for an archived team, 409 — checked before any path. */
  participantArchived: 'urn:veritant-surveys:problem:participant-archived',
  /** A team restored while another team holds its lead CRD by then, 409. */
  teamLeadCrdTaken: 'urn:veritant-surveys:problem:team-lead-crd-taken',

  // The advisor record page (SR-222).

  /** A write or clear on a staff field survey-api derives — from BrokerCheck, or from figures and
   *  rankings — 409, whatever the caller's permissions; its `managedBy` member says which. */
  fieldManaged: 'urn:veritant-surveys:problem:field-managed',
  /** Veritant's decision names, for a team's or firm's slot, a member that is no participant or
   *  not a person, 422 — checked after the slot, whose absence is a bodiless 404. */
  rosterMemberNotPerson: 'urn:veritant-surveys:problem:roster-member-not-person',
  /** A BrokerCheck refresh start while a run is `running` or `paused`, 409 — one active run in
   *  the whole database, by a partial unique index. Carries `runId`, the active run. */
  runInProgress: 'urn:veritant-surveys:problem:run-in-progress',
  /** A BrokerCheck refresh start on a database loaded anonymized, 409: its CRDs are not to be
   *  looked up. */
  anonymizedTarget: 'urn:veritant-surveys:problem:anonymized-target',
  /** Staff's IdP invitation for a disabled account, 409 (SR-442's D5): nobody could sign in with
   *  it until the account is active again. */
  accountDisabled: 'urn:veritant-surveys:problem:account-disabled',
  /** Staff's IdP invitation for an account that signs in with its identity already, 409: it is
   *  unlinked first (SR-476). */
  idpAlreadyLinked: 'urn:veritant-surveys:problem:idp-already-linked',
  /** An IdP invitation to an address another account's live invitation or linked identity
   *  holds, 409 — staff's invitation and `POST /accounts` (SR-476). */
  idpAddressTaken: 'urn:veritant-surveys:problem:idp-address-taken',
  /** The IdP exchange's 403 (SR-476): a valid token whose identity no account holds and no live
   *  invitation links — the sign-in offers to ask for an invitation. */
  accountNotLinked: 'urn:veritant-surveys:problem:account-not-linked',
  /** A ranking's scoring input the import could not read back — too many rows, too large a
   *  sheet or file — 422, refused rather than sent (SR-231, RP §9.4). */
  exportTooLarge: 'urn:veritant-surveys:problem:export-too-large',
  // The role model's account and role writes (SR-228, survey-api's spec §6.1–6.2), as its
  // `ProblemTypes.cs` spells them (checked against its task 9, the role-model UI design's §9,
  // item 6).
  /** A write on the caller's own account — 403 with a body (the roles, the status, the IdP
   *  invitation and unlink). */
  ownAccount: 'urn:veritant-surveys:problem:own-account',
  /** Roles given to an advisor account, 422: roles are a Team Portal account's alone. */
  advisorAccount: 'urn:veritant-surveys:problem:advisor-account',
  /** A role or an account would gain a permission the caller does not hold (D5), 422. */
  permissionNotHeld: 'urn:veritant-surveys:problem:permission-not-held',
  /** A permission code survey-api's catalogue does not hold, 422. */
  unknownPermission: 'urn:veritant-surveys:problem:unknown-permission',
  /** The change would leave no active account holding `roles.manage`, 422. */
  lastRoleManager: 'urn:veritant-surveys:problem:last-role-manager',
  /** A role deleted while an account still holds it, 409, with `holderCount`. */
  roleInUse: 'urn:veritant-surveys:problem:role-in-use',
  /** An account write naming role ids no role has (read a moment apart), 422, with `roleIds`. */
  unknownRole: 'urn:veritant-surveys:problem:unknown-role',
  // SR-416's address change and point-of-contact invitations (B5-13, B5-16) — block 3 reads
  // them; typed here with the contracts they answer.
  /** An address another account holds, asked for by an email change, 409. */
  emailTaken: 'urn:veritant-surveys:problem:email-taken',
  /** An address set by staff on a Team Portal account — staff set only an advisor's, 422. */
  staffAccount: 'urn:veritant-surveys:problem:staff-account',
  /** A resend or a revocation of an invitation no longer pending, 409. */
  invitationNotPending: 'urn:veritant-surveys:problem:invitation-not-pending',

  // SR-487's number checks, links and assistants (PFR §4, §8).

  /** A staff link to a participant the account is linked to already, by anything but a roster's
   *  `member` link; or an advisor's assistant invitation to an address linked to the person,
   *  409. */
  alreadyLinked: 'urn:veritant-surveys:problem:already-linked',
  /** An advisor's sixth assistant invitation while five wait, 409. */
  tooManyInvitations: 'urn:veritant-surveys:problem:too-many-invitations',
  /** A number check's assignee or decision after staff decided it, 409. */
  checkClosed: 'urn:veritant-surveys:problem:check-closed',
  /** A number check's decision that would write a number another participant holds, 409:
   *  nothing is written. */
  identifierTaken: 'urn:veritant-surveys:problem:identifier-taken',

  // The list engine (SR-230, survey-api's lists-and-reports design §5, §7, §9, §10).

  /** A staff-field write quoting a version another write has moved on from, 409 — the record
   *  page's `If-Match` on a value. Carries `current`, the cell now. A grid's batch answers the
   *  same race per cell, in its 200's `conflicts`, never with this. */
  cellConflict: 'urn:veritant-surveys:problem:cell-conflict',
  /** A seeded saved view deleted, 409: the next catalogue rebuild would plant it again. */
  seededView: 'urn:veritant-surveys:problem:seeded-view',
  /** A seeded export preset deleted, 409, for the same reason. */
  seededPreset: 'urn:veritant-surveys:problem:seeded-preset',
  /** A run asked of a saved view that is a list, 409: only a report runs. */
  notAReport: 'urn:veritant-surveys:problem:not-a-report',
  /** An import's file over the size the import takes, 413. */
  importTooLarge: 'urn:veritant-surveys:problem:import-too-large',
  /** An import's commit or reversal from a state it is not in, 409 — a second press, or an
   *  import not ready for it. */
  importState: 'urn:veritant-surveys:problem:import-state',
  /** An interview opened in a cycle that has one, 409 — `interviewId` names it. */
  ddInterviewExists: 'urn:veritant-surveys:problem:dd-interview-exists',
  /** An interview or attempt for a participant with no live response in the survey, 409. */
  noLiveResponse: 'urn:veritant-surveys:problem:no-live-response',
  /** An outreach attempt withdrawn by someone other than its recorder, 403. */
  notOutreachAuthor: 'urn:veritant-surveys:problem:not-outreach-author',
  /** Disclosures confirmed clear for a DD call while the participant has work items open in the
   *  compliance queue, 409 (SR-460) — `count` says how many. */
  openComplianceItems: 'urn:veritant-surveys:problem:open-compliance-items',

  // The recycle bin (SR-470, survey-api's recycle bin design §4).

  /** A move to the bin of a response already recycled or archived, or a restore of one not in the
   *  bin, 409 — with the `status` it is in, in the string form of `status` below. */
  transitionRefused: 'urn:veritant-surveys:problem:transition-refused',
  /** A restore while the participant holds another live response in the cycle, 409 — with the
   *  `responseId` that holds the slot. */
  liveSlotTaken: 'urn:veritant-surveys:problem:live-slot-taken',

  // Metrics updates (SR-315).

  /** Metrics updates (SR-315), 409: no cycle pinning the survey has an as-of date. */
  noRound: 'urn:veritant-surveys:problem:no-round',
  /** Metrics updates (SR-315), 409: the edition holds no threshold to read the lists by. */
  thresholdMissing: 'urn:veritant-surveys:problem:threshold-missing',
  /** Metrics updates (SR-315), 409: a condition of the list's template has no counterpart in the
   *  list engine — its `detail` names the condition. */
  templateUntranslatable: 'urn:veritant-surveys:problem:template-untranslatable',

  // Bulk mail (SR-523).

  /** A campaign's launch or a reminder step's send now while `Mail:Bulk:Enabled` is off, 409. */
  bulkMailOff: 'urn:veritant-surveys:problem:bulk-mail-off'
} as const;

export type ProblemTypeSlug = (typeof ProblemType)[keyof typeof ProblemType];

/** ProblemDetails plus the typed extensions each `type` carries. */
export interface IProblemDetails {
  type?: string;
  title?: string;
  /**
   * The HTTP status — **except** where an extension of the same name replaces it.
   * `registration-out-of-step`, `flag-not-open` and `transition-refused` each send a string
   * `status` of their own beside ProblemDetails' numeric one, the body carries both keys, and
   * `JSON.parse` keeps the last, which is the extension. So a number here is the HTTP status and
   * a string is the extension; the HTTP status is always read off `ApiError.status`, never off
   * this.
   */
  status?: number | string;
  detail?: string;
  /**
   * `revision-conflict` — the revision the server actually holds. Sent by every route that
   * takes `If-Match` (the authoring drafts, a staff correction, a work item's `PATCH` and
   * reopening, the refresh schedule's `PUT`) and by the respondent's writes — **except** by
   * two producers that answer the same slug without it: a database save that lost a race
   * (survey-api's `ConcurrencyExceptionHandler`) and a work-item batch that lost one. So a
   * caller may read it and must never require it.
   */
  currentRevision?: number;
  /** `response-not-writable` — locked / recycled / archived. */
  status_?: string;
  /** `response-exists` — the live response already open for this cycle; `live-slot-taken` — the
   *  live response holding the slot a restore asked for. */
  responseId?: string;
  /** `writes-rejected` — every refused write and why. */
  rejections?: IWriteRejection[];
  /** `submit-blocked` — the findings report the review page renders from. */
  report?: unknown;
  /** `publish-failed` — the same finding list the dry run answers with. */
  findings?: { severity: string; code: string; message: string; subject: Nullable<string> }[];
  /** One entry per refused item — shared by `invalid-action`, `invalid-query` and
   *  `invalid-body`, so a client parses one error body across the whole admin surface. */
  problems?: { target: string; reason: string }[];
  /* `flag-not-open`'s own `status` is the string form of `status` above. The flags screen
     does not read it: a refused transition re-reads the flags. */
  /** `registration-out-of-step` — the step the run is actually on. Its run status is the
   *  string form of `status` above; `registrationRunStatus` reads it. */
  step?: string;
  /** `registration-refused` — the holder's own addresses, masked (`j•••@domain`), `[]` for
   *  `address_unavailable` and for a holder no `self` account acts for. */
  heldBy?: string[];
  /** `registration-refused` — `identifier_unavailable` or `address_unavailable`; a work item's
   *  `invalid-transition` — survey-api's sentence of why the state refused the move; a refresh
   *  run's `invalid-transition` — the state the run is in (`completed` or `cancelled`). */
  reason?: string;
  /** `run-in-progress` — the BrokerCheck refresh run that is running or paused. */
  runId?: string;
  /** A work item's `invalid-transition` — every item id the move was refused for. */
  itemIds?: string[];
  /** `identifier-already-yours` — the caller's own participant that holds the number. */
  participantId?: string;
  /** `team-routing-closed` — who decided the routing: `staff` or `firm`. */
  source?: string;
  /** `open-compliance-items` — the participant's unclosed work items. */
  count?: number;
  /** `cell-conflict` — the cell as it stands now (`ICurrentCell` in `lists-contracts.ts`), read
   *  by the domain that sent the write. */
  current?: unknown;
  /** ASP.NET validation problems (`/auth/*` missing-field shapes). */
  errors?: Record<string, string[]>;
  [key: string]: unknown;
}

export interface IWriteRejection {
  usage: string;
  instanceId: Nullable<string>;
  instanceKey: string;
  reason: string;
}

/**
 * A non-2xx answer from survey-api. Carries the parsed problem document so
 * callers can branch on `type` — `isProblem` is the only way they should.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Nullable<IProblemDetails>;

  constructor(status: number, problem: Nullable<IProblemDetails>, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  get problemType(): Nullable<string> {
    return this.problem?.type ?? null;
  }

  isProblem(type: ProblemTypeSlug): boolean {
    return this.problem?.type === type;
  }
}

/** The run status a `registration-out-of-step` refusal carries — `in_progress`, `completed` or
 *  `abandoned` — or null for any other error. Read as a string or not at all, because the
 *  member it travels in is also ProblemDetails' numeric one (see `IProblemDetails.status`). */
export function registrationRunStatus(error: unknown): Nullable<string> {
  if (!(error instanceof ApiError) || !error.isProblem(ProblemType.registrationOutOfStep)) {
    return null;
  }
  const status = error.problem?.status;
  return typeof status === 'string' ? status : null;
}

/** The `reason` a `registration-refused` carries — `identifier_unavailable` or
 *  `address_unavailable` — or null for any other error. The two end in different places (the
 *  first ends the registration, the second leaves it open for another address), which is why
 *  it is read here rather than inferred from the step the refusal arrived on. */
export function registrationRefusal(error: unknown): Nullable<string> {
  if (!(error instanceof ApiError) || !error.isProblem(ProblemType.registrationRefused)) {
    return null;
  }
  return error.problem?.reason ?? null;
}

/** The addresses a `registration-refused` for a taken number names — the holder's own, masked,
 *  each sent an invitation to sign in by the refusal itself (SR-487, PFR §2.4) — or `[]` for any
 *  other error and for a refusal that names none. Strings alone are kept: the list is drawn. */
export function registrationHeldBy(error: unknown): string[] {
  if (!(error instanceof ApiError) || !error.isProblem(ProblemType.registrationRefused)) {
    return [];
  }
  const heldBy = error.problem?.heldBy;
  return Array.isArray(heldBy) ? heldBy.filter((held) => typeof held === 'string') : [];
}

/** The caller's own participant an `identifier-already-yours` names — the one that already
 *  holds the number a signed-in registration offered — or null for any other error. The only
 *  registration refusal that names anybody, because the holder is the caller. */
export function identifierAlreadyYours(error: unknown): Nullable<string> {
  if (!(error instanceof ApiError) || !error.isProblem(ProblemType.identifierAlreadyYours)) {
    return null;
  }
  return error.problem?.participantId ?? null;
}

/** Best-effort human text for an error with no UI of its own. */
export function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.problem?.detail || error.problem?.title || error.message || fallback;
  }
  return (error as Error)?.message || fallback;
}
